import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";

const faqs: LearnFAQ[] = [
  {
    question: "Which is more effective, ECT or SAINT®?",
    answer:
      "ECT has the longest track record and the highest remission rates for severe, psychotic, or catatonic depression. SAINT® has published randomized trial data for treatment-resistant depression — 79% of participants reached remission in the 2022 trial (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. The two have never been compared head-to-head, so neither can be called more effective for every patient. The right choice depends on severity, urgency, and whether you can undergo anesthesia.",
  },
  {
    question: "Can I do both SAINT® and ECT?",
    answer:
      "They are rarely used at the same time, but one may be an option if the other has not provided enough relief. ECT requires general anesthesia and a hospital setting; SAINT® is non-invasive and outpatient. Whether switching or combining is appropriate is a clinical decision that depends on your diagnosis, severity, and contraindications. A psychiatric evaluation determines which may be right for you.",
  },
  {
    question: "Which lasts longer, SAINT® or ECT?",
    answer:
      "Both can require follow-up. SAINT® has published one-year follow-up data: most participants who reached remission maintained it at one year (Cole et al., Am J Psychiatry 2020;177(10):970–978). Individual results may vary. ECT also often requires continuation or maintenance sessions to sustain benefit. Durability varies from person to person for both, and neither is a cure.",
  },
  {
    question: "Does SAINT® cause memory loss like ECT?",
    answer:
      "No. SAINT® uses magnetic pulses without inducing a seizure or using anesthesia. The memory and cognitive side effects associated with ECT are not reported side effects of SAINT® or conventional TMS. SAINT® side effects are typically headache, scalp discomfort, and facial twitching — usually mild and temporary. TMS carries a small risk of seizure.",
  },
  {
    question: "Is SAINT® an alternative to ECT?",
    answer:
      "For some adults with treatment-resistant depression, SAINT® may be an option instead of ECT. SAINT® is non-invasive, outpatient, and requires no anesthesia. ECT remains a highly effective option for severe, psychotic, or catatonic depression and when rapid response is critical. A psychiatric evaluation determines which is appropriate for your situation.",
  },
  {
    question: "Does insurance cover ECT or SAINT®?",
    answer:
      "ECT is generally covered by insurance for its approved indications. SAINT® may be covered; coverage varies by plan. Neuro Wellness verifies your benefits for free, whatever carrier you have, so you know what your plan allows before you commit. Financing is available for SAINT® on a case-by-case basis and is discussed during your consultation.",
  },
];

export const pageSaintVsEct = makeArticle({
  slug: "saint-vs-ect",
  title: "SAINT® TMS vs ECT: What's the Difference?",
  description:
    "SAINT® vs ECT for depression: non-invasive magnetic stimulation vs electrical seizure therapy. Compare FDA status, anesthesia, schedule, side effects, cost and durability.",
  canonical: "/learn/saint-vs-ect",
  h1: "SAINT® TMS vs ECT: Understanding the Differences",
  breadcrumbLabel: "SAINT vs ECT",
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) and ECT (electroconvulsive therapy) are both brain stimulation treatments for depression, but they work very differently. SAINT® uses fMRI-guided magnetic pulses over 5 consecutive days — no anesthesia, no seizure, outpatient. ECT uses electrical current to induce a controlled seizure under general anesthesia in a hospital. SAINT® is FDA-cleared for major depressive disorder in adults whose depression has not improved enough on prior antidepressant medication. ECT is a long-established treatment often used for severe, psychotic, or catatonic depression. ECT has the longest track record and the highest remission rates for severe depression but carries anesthesia and cognitive risks. The right choice depends on severity, urgency, and clinical circumstances, and should be discussed with a psychiatrist.",
  keyTakeaways: [
    "SAINT® uses fMRI-guided magnetic pulses; ECT uses electrical current to induce a controlled seizure under general anesthesia.",
    "SAINT® requires no anesthesia and is outpatient; ECT requires general anesthesia and is hospital-based.",
    "SAINT® is FDA-cleared (K220177) for major depressive disorder in adults whose depression has not improved enough on prior antidepressant medication.",
    "ECT has the longest track record and the highest remission rates for severe, psychotic, or catatonic depression.",
    "ECT carries cognitive and anesthesia risks; SAINT® does not require anesthesia and has no reported cognitive side effects.",
    "SAINT® at Neuro Wellness starts at $25,000; ECT is generally covered by insurance for its approved indications.",
  ],
  sections: [
    {
      heading: "Quick answer: SAINT® vs ECT for treatment-resistant depression",
      paragraphs: [
        "If you are comparing SAINT® and ECT, you are comparing two brain stimulation treatments for depression that work through very different mechanisms. SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is delivered on the Magnus Medical SAINT Neuromodulation System. It uses a functional MRI (fMRI) scan to personalize the treatment target for each person, then delivers 10 sessions per day over 5 consecutive days — about 50 sessions total. It is non-invasive: a coil placed on the scalp delivers magnetic pulses, with no needles, no drugs, and no anesthesia. It is FDA-cleared for major depressive disorder (MDD) in adults whose depression has not improved enough on prior antidepressant medication. Neuro Wellness treats adults 22 and older.",
        "ECT (electroconvulsive therapy) passes electrical current through the brain to induce a controlled seizure while you are under general anesthesia. It is performed in a hospital or procedure suite with an anesthesiologist present. ECT has been used for decades and has the longest track record of any brain stimulation treatment for depression. It is often reserved for severe, psychotic, or catatonic depression, depression with suicidal urgency, or when rapid response is critical.",
        "Both are legitimate treatment options. The question is which fits your diagnosis, severity, and whether you can undergo anesthesia. Learn more about [[SAINT®|/saint]] and take the [[free candidacy screener|/am-i-a-candidate-screener]] to see which may apply to you. For more on what treatment-resistant depression means, see our [[treatment-resistant depression guide|/learn/what-is-treatment-resistant-depression]].",
      ],
    },
    {
      heading: "How each treatment works",
      paragraphs: [
        "SAINT® uses intermittent theta burst stimulation (iTBS) — a pattern of magnetic pulses that induces electrical currents in the prefrontal cortex, the area of the brain involved in mood regulation. The fMRI scan is used to find the exact spot on your scalp that best connects to a mood-regulating brain circuit, so the stimulation is personalized to your brain. No seizure is induced. You sit awake in a chair while a coil on your scalp delivers the pulses. Nothing enters your bloodstream.",
        "ECT passes electrical current through the brain to deliberately induce a controlled seizure. The seizure activity is what is thought to produce the therapeutic effect. Because a seizure is induced, ECT requires general anesthesia — you are asleep and unaware during the procedure — and is performed in a hospital or procedure suite with an anesthesiologist and nursing team. You wake up in a recovery area and cannot drive yourself home afterward.",
        "The fundamental difference: SAINT® is a non-invasive device that stimulates the brain magnetically without inducing a seizure; ECT is a procedure that uses electricity to induce a seizure under anesthesia. Both affect brain circuits involved in depression, but through different mechanisms and with very different treatment experiences.",
      ],
    },
    {
      heading: "Key differences: SAINT® vs ECT",
      paragraphs: [
        "The table below summarizes the key differences between SAINT® and ECT at a glance.",
      ],
      table: {
        headers: ["", "SAINT® (Magnus)", "ECT"],
        rows: [
          [
            "FDA status",
            "FDA-cleared system (K220177) for MDD in adults with inadequate response to prior antidepressants; Breakthrough Device designation",
            "Long-established treatment; widely performed for decades; not a single-device clearance but a standard of care for severe depression",
          ],
          [
            "Mechanism",
            "Magnetic pulses (iTBS) — no seizure induced",
            "Electrical current inducing a controlled seizure",
          ],
          [
            "Anesthesia",
            "None — you sit awake in a chair",
            "General anesthesia required; anesthesiologist present",
          ],
          [
            "Setting",
            "Outpatient clinic",
            "Hospital or procedure suite with recovery area",
          ],
          [
            "Sessions",
            "10 sessions/day × 5 days; ~50 sessions total",
            "2–3 sessions/week for 2–4 weeks; 6–12 total",
          ],
          [
            "Time to response",
            "Response typically measured within the 5-day week",
            "Response typically within 2–4 weeks as sessions accumulate",
          ],
          [
            "Memory / cognitive effects",
            "None reported; no seizure or anesthesia",
            "Possible memory loss (usually temporary, sometimes persistent); cognitive effects possible",
          ],
          [
            "Driving after session",
            "Generally yes — no sedation",
            "No — cannot drive after each session due to anesthesia",
          ],
          [
            "Indication",
            "MDD in adults after inadequate response to prior antidepressants",
            "Severe, psychotic, or catatonic depression; suicidal urgency; when rapid response is critical",
          ],
          [
            "Cost / insurance",
            "Starts at $25,000 at NWCOA; may be covered, varies by plan; financing available",
            "Generally covered by insurance for approved indications; hospital and anesthesia costs apply",
          ],
        ],
      },
    },
    {
      heading: "When each treatment is used",
      paragraphs: [
        "SAINT® is FDA-cleared for adults with major depressive disorder whose depression has not improved enough on prior antidepressant medication. It is an outpatient treatment suitable for people who want a non-invasive option and who can commit to 5 consecutive days in the clinic. Because it does not require anesthesia, it avoids the risks and recovery time associated with general anesthesia. It is not for people with non-removable magnetic-sensitive metal near the head, certain implanted devices, or increased intracranial pressure.",
        "ECT is often reserved for the most severe forms of depression — psychotic depression, catatonic depression, or depression with suicidal urgency where rapid response is critical. It is also used when other treatments have not worked. ECT has the longest track record of any brain stimulation treatment for depression and has the highest remission rates for severe depression. However, it carries the risks of general anesthesia and the possibility of memory and cognitive side effects.",
        "Neither is universally better. The appropriate choice depends on the severity of your depression, how urgently you need relief, whether you can safely undergo anesthesia, and your overall medical history. A psychiatric evaluation is the best way to determine which is right for you.",
      ],
    },
    {
      heading: "Durability: how long each treatment lasts",
      paragraphs: [
        "Durability — how long the benefit lasts after the treatment course ends — is an important consideration for both SAINT® and ECT.",
        "SAINT® has published follow-up data. In the 2020 open-label study, most participants who reached remission maintained it at the one-year follow-up (Cole et al., Am J Psychiatry 2020;177(10):970–978). Individual results may vary. SAINT® is delivered as a discrete 5-day course; there is no routine schedule of booster sessions, though follow-up and maintenance are part of the care plan.",
        "ECT also often requires continuation or maintenance sessions after the acute course to sustain benefit. Without maintenance, relapse rates can be significant. The frequency of maintenance ECT varies — some patients receive it weekly, monthly, or less often depending on response. Durability varies from person to person for both treatments, and neither is a cure.",
        "In short: SAINT® aims for a durable response from a single one-week course; ECT often requires an acute course followed by ongoing maintenance to sustain any benefit. Individual results may vary for both.",
      ],
    },
    {
      heading: "Cost and insurance",
      paragraphs: [
        "SAINT® at Neuro Wellness starts at $25,000. That price reflects the fMRI scan, the FDA-cleared targeting software, the Magnus system, physician oversight, and the full 50-session week. SAINT® may be covered by insurance; coverage varies by plan. We verify your benefits for free, whatever carrier you have, so you know what your plan allows before you commit. Financing is available for SAINT® on a case-by-case basis and is discussed during your consultation.",
        "ECT is generally covered by insurance for its approved indications because it is a long-established standard of care. However, ECT involves hospital facility fees, anesthesia costs, and recovery time, which can add up. The total cost of an ECT course varies by hospital, number of sessions, and your insurance plan. Coverage varies by plan for all of these treatments.",
        "For the full SAINT® cost breakdown, see our [[SAINT® TMS cost page|/learn/saint-tms-cost]]. To check what your plan covers, start with a [[free benefits verification|/check-insurance-options]].",
      ],
    },
    {
      heading: "Can I combine them or switch?",
      paragraphs: [
        "SAINT® and ECT are rarely used at the same time, but one may be an option if the other has not provided enough relief. Because they work through different mechanisms — magnetic stimulation without a seizure vs electrical induction of a seizure — they are not interchangeable, and one may be appropriate when the other has not worked.",
        "If you have already had ECT and are considering SAINT®, or vice versa, whether switching is appropriate is a clinical decision that depends on your diagnosis, treatment history, and contraindications. SAINT® is contraindicated for people with non-removable magnetic-sensitive metal near the head, certain implanted devices, or increased intracranial pressure. ECT requires that you be cleared for general anesthesia.",
        "A [[free consultation|/contactus]] is the best way to explore what fits your history. For more comparisons, see our guides to [[SAINT® vs ketamine|/learn/saint-vs-ketamine]], [[SAINT® vs SPRAVATO®|/learn/saint-vs-spravato]], and [[is SAINT® TMS safe|/learn/is-saint-tms-safe]].",
      ],
    },
    {
      heading: "Who each treatment fits",
      paragraphs: [
        "SAINT® fits adults with major depressive disorder whose depression has not improved enough on prior antidepressant medication and who can commit to 5 consecutive days in the clinic. The compressed schedule suits people who want to avoid weeks of visits and who want a protocol with fMRI-guided personalized targeting and published randomized trial data. It is not for people with non-removable magnetic-sensitive metal near the head, certain implanted devices, or increased intracranial pressure.",
        "ECT may be appropriate for adults with severe, psychotic, or catatonic depression, or depression with suicidal urgency where rapid response is critical. Because ECT requires general anesthesia and a hospital setting, it suits someone who is medically cleared for anesthesia and who understands the possibility of memory and cognitive side effects. ECT has the longest track record and the highest remission rates for the most severe forms of depression.",
        "Neuro Wellness offers SAINT® and standard TMS, along with SPRAVATO® (esketamine), IV ketamine, psychotherapy, and medication management. We do not offer ECT. A [[free consultation|/contactus]] is the best way to learn which fits your diagnosis and circumstances. Call 954-827-9793 to speak with our team.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, Stimpson KH, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2022;179(2):132–141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.21060580",
    },
    {
      citation:
        "Cole EJ, Phillips AL, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression: 1-year follow-up. Am J Psychiatry. 2020;177(10):970–978.",
      url: "https://doi.org/10.1176/appi.ajp.2020.20040470",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification K220177 — Magnus Medical SAINT Neuromodulation System.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177",
    },
    {
      citation:
        "American Psychiatric Association. The Practice of Electroconvulsive Therapy: Recommendations for Treatment, Training, and Privileging (2nd ed.). APA Task Force on ECT.",
      url: "https://psychiatry.org/psychiatrists/practice/clinical-practice-resources/ect",
    },
    {
      citation:
        "UK ECT Review Group. Efficacy and safety of electroconvulsive therapy in depressive disorders: a systematic review and meta-analysis. Lancet. 2003;361(9360):799–808.",
      url: "https://doi.org/10.1016/S0140-6736(03)12701-0",
    },
  ],
  about: [
    {
      "@type": "MedicalTherapy",
      name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
      description:
        "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System: 10 sessions per day for 5 consecutive days.",
    },
    {
      "@type": "MedicalTherapy",
      name: "Electroconvulsive Therapy (ECT)",
      description:
        "A psychiatric treatment in which electrical currents are passed through the brain to induce a controlled seizure under general anesthesia; a long-established treatment for severe depression.",
    },
  ],
  relatedLinks: [
    {
      title: "SAINT® vs Ketamine",
      href: "/learn/saint-vs-ketamine",
      description: "How SAINT® compares to IV ketamine for depression",
    },
    {
      title: "SAINT® vs SPRAVATO®",
      href: "/learn/saint-vs-spravato",
      description: "How SAINT® compares to esketamine nasal spray",
    },
    {
      title: "Is SAINT® TMS Safe?",
      href: "/learn/is-saint-tms-safe",
      description: "Safety, side effects, and contraindications",
    },
    {
      title: "SAINT® TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "What SAINT® costs and how to plan for payment",
    },
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol offered at our clinic",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate-screener",
      description: "Free candidacy screener",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    {
      label: "Quick Answer",
      href: "#quick-answer-saint-vs-ect-for-treatment-resistant-depression",
    },
    { label: "How Each Works", href: "#how-each-treatment-works" },
    { label: "Key Differences", href: "#key-differences-saint-vs-ect" },
    { label: "When Each Is Used", href: "#when-each-treatment-is-used" },
    { label: "Durability", href: "#durability-how-long-each-treatment-lasts" },
    { label: "Cost & Insurance", href: "#cost-and-insurance" },
    {
      label: "Can I Combine or Switch?",
      href: "#can-i-combine-them-or-switch",
    },
    { label: "Who Each Fits", href: "#who-each-treatment-fits" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
